import { css, useTheme } from '@emotion/react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useFolders } from '@/hooks/use-folders';
import { updateProject } from '@/lib/api-client';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface MoveScriptDialogProps {
  isOpen: boolean;
  scriptId: string;
  scriptTitle: string;
  currentFolderId: string | null;
  onMoved: () => void;
  onCancel: () => void;
}

export function MoveScriptDialog({
  isOpen,
  scriptId,
  scriptTitle,
  currentFolderId,
  onMoved,
  onCancel,
}: MoveScriptDialogProps) {
  const theme = useTheme();
  const { folders, refetch: refetchFolders } = useFolders();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(currentFolderId);
  const [isMoving, setIsMoving] = useState(false);

  if (!isOpen) return null;

  const handleMove = async () => {
    if (selectedFolderId === currentFolderId) {
      onCancel();
      return;
    }
    setIsMoving(true);
    try {
      await updateProject(scriptId, { folderId: selectedFolderId });
      refetchFolders();
      onMoved();
    } catch {
      // Error handled by caller
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        background: ${theme.colors.overlay};
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50;
      `}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-dialog-title"
        css={css`
          background: ${theme.colors.surfaceElevated};
          border: 1px solid ${theme.colors.border};
          border-radius: ${radii.md};
          padding: ${spacing.xl};
          max-width: 400px;
          width: 90%;
          box-shadow: ${shadows.prominent};
        `}
      >
        <h2
          id="move-dialog-title"
          css={css`
            margin: 0 0 ${spacing.sm};
            font-size: ${fontSizes.xl};
            color: ${theme.colors.text};
          `}
        >
          Move &ldquo;{scriptTitle}&rdquo;
        </h2>
        <p
          css={css`
            margin: 0 0 ${spacing.md};
            color: ${theme.colors.textSecondary};
            font-size: ${fontSizes.sm};
          `}
        >
          Select a folder:
        </p>
        <ul
          css={css`
            list-style: none;
            padding: 0;
            margin: 0 0 ${spacing.xl};
            display: flex;
            flex-direction: column;
            gap: 2px;
          `}
        >
          <li>
            <button
              type="button"
              onClick={() => setSelectedFolderId(null)}
              css={css`
                display: block;
                width: 100%;
                text-align: left;
                padding: ${spacing.sm} ${spacing.md};
                border-radius: ${radii.sm};
                border: none;
                cursor: pointer;
                font-size: ${fontSizes.sm};
                background: ${selectedFolderId === null
                  ? theme.colors.sidebarActiveBg
                  : 'transparent'};
                color: ${selectedFolderId === null
                  ? theme.colors.sidebarActiveText
                  : theme.colors.text};
                font-weight: ${selectedFolderId === null ? 600 : 400};

                &:hover {
                  background: ${selectedFolderId === null
                    ? theme.colors.sidebarActiveBg
                    : theme.colors.sidebarHoverBg};
                }
              `}
            >
              No folder
            </button>
          </li>
          {folders.map((folder) => (
            <li key={folder.id}>
              <button
                type="button"
                onClick={() => setSelectedFolderId(folder.id)}
                css={css`
                  display: block;
                  width: 100%;
                  text-align: left;
                  padding: ${spacing.sm} ${spacing.md};
                  border-radius: ${radii.sm};
                  border: none;
                  cursor: pointer;
                  font-size: ${fontSizes.sm};
                  background: ${selectedFolderId === folder.id
                    ? theme.colors.sidebarActiveBg
                    : 'transparent'};
                  color: ${selectedFolderId === folder.id
                    ? theme.colors.sidebarActiveText
                    : theme.colors.text};
                  font-weight: ${selectedFolderId === folder.id ? 600 : 400};

                  &:hover {
                    background: ${selectedFolderId === folder.id
                      ? theme.colors.sidebarActiveBg
                      : theme.colors.sidebarHoverBg};
                  }
                `}
              >
                {folder.name}
              </button>
            </li>
          ))}
        </ul>
        <div
          css={css`
            display: flex;
            gap: ${spacing.sm};
            justify-content: flex-end;
          `}
        >
          <Button variant="secondary" shape="rect" size="sm" onClick={onCancel} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            shape="rect"
            size="sm"
            onClick={handleMove}
            disabled={isMoving}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.xs};
            `}
          >
            {isMoving && <Spinner size="sm" aria-hidden />}
            {isMoving ? 'Moving…' : 'Move'}
          </Button>
        </div>
      </div>
    </div>
  );
}
